import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useActivityFilters } from '../hooks/useActivityFilters';
import { useActivities, useTags, useTransitions, useCategories } from '../hooks/useActivities';
import { useGenerationJob } from '../hooks/useGenerationJobs';
import { ActivitiesTable } from '../components/ActivitiesTable';
import { SearchBar } from '../components/SearchBar';
import { FilterControls } from '../components/FilterControls';
import { ActiveFilters } from '../components/ActiveFilters';
import { Pagination } from '../components/Pagination';
import type { ActivityFilters } from '../types/activity';

/**
 * Helper to check if error is an Error object
 */
function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Activities list page with search, filters, and pagination
 */
export default function ActivitiesPage() {
  const navigate = useNavigate();

  // Update document title
  useEffect(() => {
    document.title = 'Activities | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  // Get filter state and updaters
  const {
    filters,
    updateFilter,
    clearFilters,
    clearFilter,
    hasActiveFilters,
  } = useActivityFilters();

  // Fetch activities with React Query
  const { data, isLoading, isError: hasError, error } = useActivities(filters);

  // Fetch available tags
  const { data: tagsData, isLoading: isLoadingTags } = useTags();
  const availableTags = tagsData?.data || [];

  // Fetch available transitions
  const { data: transitionsData, isLoading: isLoadingTransitions } = useTransitions();
  const availableTransitions = transitionsData?.data || [];

  // Fetch available categories
  const { data: categoriesData, isLoading: isLoadingCategories } = useCategories();
  const availableCategories = categoriesData?.data || [];

  // Fetch job context for the generationJobId chip label
  const { data: jobDetailData } = useGenerationJob(filters.generationJobId || '');
  const jobContext = jobDetailData?.data
    ? { id: jobDetailData.data.id, createdAt: jobDetailData.data.createdAt }
    : null;

  // Show error toast if query fails
  useEffect(() => {
    if (hasError && error) {
      const errorMessage = isError(error) ? error.message : 'Failed to load activities';
      toast.error(errorMessage);
    }
  }, [hasError, error]);

  // Memoized search change handler
  const handleSearchChange = useCallback(
    (value: string) => {
      updateFilter('search', value);
    },
    [updateFilter]
  );

  // Handle column sorting
  const handleSort = useCallback(
    (column: ActivityFilters['sortBy']) => {
      if (filters.sortBy === column) {
        // Toggle sort order if clicking the same column
        updateFilter('sortOrder', filters.sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        // Set new column with default descending order
        updateFilter('sortBy', column);
        updateFilter('sortOrder', 'desc');
      }
    },
    [filters.sortBy, filters.sortOrder, updateFilter]
  );

  // Handle removing individual tag from filter
  const handleRemoveTag = useCallback(
    (tagId: number) => {
      const newTagIds = filters.tagIds.filter((id) => id !== tagId);
      updateFilter('tagIds', newTagIds);
    },
    [filters.tagIds, updateFilter]
  );

  // Handle removing individual transition from filter
  const handleRemoveTransition = useCallback(
    (transitionId: number) => {
      const newTransitionIds = filters.transitionIds.filter((id) => id !== transitionId);
      updateFilter('transitionIds', newTransitionIds);
    },
    [filters.transitionIds, updateFilter]
  );

  // Handle removing individual age group from filter
  const handleRemoveAgeGroup = useCallback(
    (key: string) => {
      const newKeys = filters.ageGroupKeys.filter((k) => k !== key);
      updateFilter('ageGroupKeys', newKeys);
    },
    [filters.ageGroupKeys, updateFilter]
  );

  // Handle removing individual category from filter
  const handleRemoveCategory = useCallback(
    (categoryId: number) => {
      const newIds = filters.categoryIds.filter((id) => id !== categoryId);
      updateFilter('categoryIds', newIds);
    },
    [filters.categoryIds, updateFilter]
  );

  // Calculate total pages
  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0;
  
  // Check if database is completely empty
  const hasNoActivitiesAtAll = data?.total === 0 && !hasActiveFilters;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-heading-1 text-admin-slate-400 mb-2">Activities</h1>
          <p className="text-body text-admin-slate-500">
            Manage all activities in your platform
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/activities/new')}
          className="admin-button-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          <span>New Activity</span>
        </button>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <SearchBar
          value={filters.search}
          onChange={handleSearchChange}
          placeholder="Search by title, description, steps, tips... (⌘K)"
        />
      </motion.div>

      {/* Filter Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <FilterControls
          filters={filters}
          onFilterChange={updateFilter}
          availableTags={availableTags}
          isLoadingTags={isLoadingTags}
          availableTransitions={availableTransitions}
          isLoadingTransitions={isLoadingTransitions}
          availableCategories={availableCategories}
          isLoadingCategories={isLoadingCategories}
        />
      </motion.div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <ActiveFilters
            filters={filters}
            onClearFilter={clearFilter}
            onClearAll={clearFilters}
            hasActiveFilters={hasActiveFilters}
            availableTags={availableTags}
            onRemoveTag={handleRemoveTag}
            availableTransitions={availableTransitions}
            onRemoveTransition={handleRemoveTransition}
            onRemoveAgeGroup={handleRemoveAgeGroup}
            availableCategories={availableCategories}
            onRemoveCategory={handleRemoveCategory}
            jobContext={jobContext}
          />
        </motion.div>
      )}

      {/* Error State */}
      {hasError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="admin-card p-12 text-center"
        >
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-heading-4 text-admin-slate-400 mb-2">
              Failed to load activities
            </h3>
            <p className="text-body-sm text-admin-slate-500 mb-6">
              {isError(error) ? error.message : 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="admin-button-primary"
            >
              Retry
            </button>
          </div>
        </motion.div>
      )}

      {/* Activities Table */}
      {!hasError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <ActivitiesTable
            activities={data?.items || []}
            isLoading={isLoading}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            onSort={handleSort}
            isEmpty={hasNoActivitiesAtAll}
            onCreateFirst={() => navigate('/admin/activities/new')}
          />
        </motion.div>
      )}

      {/* Pagination */}
      {!hasError && data && totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="admin-card"
        >
          <Pagination
            currentPage={filters.page}
            totalPages={totalPages}
            totalItems={data.total}
            pageSize={filters.pageSize}
            onPageChange={(page) => updateFilter('page', page)}
          />
        </motion.div>
      )}
    </div>
  );
}

